import { useState, useEffect, useCallback } from 'react';

import {
  buildTree as buildTreeFrom,
  nextSortOrder as nextSortOrderFor,
  countDescendants as countDescendantsOf,
  reparentTarget,
} from '../lib/orgTree';
import { zrodlaPoEdycjiCzlowieka, type Zrodla } from '../lib/pochodzeniePol';
import { supabase } from '../lib/supabase';
import { useAuth } from '../providers/AuthProvider';

export type UnitType = 'department' | 'position' | 'team';

export interface OrganizationalUnit {
  id: string;
  organization_id: string;
  parent_id: string | null;
  unit_type: UnitType;
  name: string;
  code: string | null;
  description: string | null;
  color: string | null;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
  manager_id: string | null;
  cost_center_code: string | null;
  // Base-table-only columns — vw_organizational_tree does not project them
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
  // Computed from view
  depth?: number;
  employee_count?: number;
  equipment_count?: number;
  direct_children_count?: number;
  full_path_name?: string;
}

export interface OrgTreeNode extends OrganizationalUnit {
  children: OrgTreeNode[];
}

export interface CreateUnitInput {
  parent_id?: string | null;
  unit_type: UnitType;
  name: string;
  code?: string;
  description?: string;
  color?: string;
  icon?: string;
  sort_order?: number;
}

export interface UpdateUnitInput {
  name?: string;
  code?: string;
  description?: string;
  color?: string;
  icon?: string;
  sort_order?: number;
  is_active?: boolean;
  parent_id?: string | null;
  manager_id?: string | null;
  cost_center_code?: string;
}

export interface OrganizationProfile {
  id?: string;
  organization_id: string;
  mission: string | null;
  goals: string | null;
  policies: string | null;
  analysis: string | null;
  differentiators: string | null;
  assets_description: string | null;
  cost_structure: string | null;
  extended_data: Record<string, unknown>;
  /** Pochodzenie pól (migracja 689): {pole: {zrodlo, zrodlo_url?, pewnosc?, zapisano_at}}. */
  zrodla?: Zrodla;
  /** Przebieg Genesis, z którego synteza napisała profil; NULL = sprzed jednej prawdy. */
  synteza_run_id?: string | null;
  zsyntetyzowano_at?: string | null;
}

export interface SaveProfileOpts {
  /**
   * `'czlowiek'` = zapis z formularza: pola, których wartość faktycznie się
   * zmieniła względem wczytanego profilu, dostają `zrodla[pole] = czlowiek`
   * — i od tej chwili żaden research ani synteza ich nie nadpisze (§4.1).
   */
  zrodloZmian?: 'czlowiek';
}

/** Klucze profilu porównywane przy zapisie ręcznym; `extended_data` osobno, po kluczach. */
const POLA_TEKSTOWE_PROFILU = [
  'mission',
  'goals',
  'policies',
  'analysis',
  'differentiators',
  'assets_description',
  'cost_structure',
] as const;

/**
 * `zrodla` po zapisie: dla zapisu człowieka liczy diff względem `poprzedni`
 * (pola tekstowe + każdy klucz `extended_data`) i dopisuje `czlowiek`; dla
 * zapisu automatu bierze `input.zrodla` (już scalone przez wołającego) albo
 * zostawia dotychczasowe. Czysta — wyniesiona z hooka, żeby dało się ją zmierzyć.
 */
export function zrodlaDoZapisu(
  poprzedni: OrganizationProfile | null,
  input: Partial<OrganizationProfile>,
  opts?: SaveProfileOpts,
  teraz: Date = new Date()
): Zrodla {
  const baza: Zrodla = { ...(poprzedni?.zrodla ?? {}), ...(input.zrodla ?? {}) };
  if (opts?.zrodloZmian !== 'czlowiek') return baza;

  const przedtem: Record<string, unknown> = {};
  const potem: Record<string, unknown> = {};
  for (const pole of POLA_TEKSTOWE_PROFILU) {
    if (!(pole in input)) continue;
    przedtem[pole] = poprzedni?.[pole] ?? null;
    potem[pole] = input[pole] ?? null;
  }
  if (input.extended_data) {
    const stare = poprzedni?.extended_data ?? {};
    for (const klucz of Object.keys(input.extended_data)) {
      przedtem[klucz] = stare[klucz];
      potem[klucz] = input.extended_data[klucz];
    }
  }
  return zrodlaPoEdycjiCzlowieka(baza, przedtem, potem, teraz).zrodla;
}

const buildTree = (units: OrganizationalUnit[]): OrgTreeNode[] =>
  buildTreeFrom(units) as OrgTreeNode[];

export function useOrganizationalStructure(organizationId?: string) {
  const { currentOrganization } = useAuth();
  const orgId = organizationId || currentOrganization?.organization_id;

  const [units, setUnits] = useState<OrganizationalUnit[]>([]);
  const [tree, setTree] = useState<OrgTreeNode[]>([]);
  const [profile, setProfile] = useState<OrganizationProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUnits = useCallback(async () => {
    if (!orgId) return;

    try {
      // Read through vw_organizational_tree, not the base table: `depth`,
      // `employee_count`, `equipment_count`, `direct_children_count` and
      // `full_path_name` only exist on the view. OrgChart renders the employee
      // and equipment counters, so selecting the base table here made them
      // permanently undefined — the counters never appeared.
      const { data, error: fetchError } = await supabase
        .from('vw_organizational_tree')
        .select('*')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (fetchError) throw fetchError;

      const unitList = (data || []) as OrganizationalUnit[];
      setUnits(unitList);
      setTree(buildTree(unitList));
    } catch (err: any) {
      console.error('Error fetching organizational units:', err);
      setError(err.message);
    }
  }, [orgId]);

  const fetchProfile = useCallback(async () => {
    if (!orgId) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('organization_profiles')
        .select('*')
        .eq('organization_id', orgId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setProfile(data as OrganizationProfile | null);
    } catch (err: any) {
      console.error('Error fetching org profile:', err);
    }
  }, [orgId]);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    await Promise.all([fetchUnits(), fetchProfile()]);
    setIsLoading(false);
  }, [fetchUnits, fetchProfile]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const nextSortOrder = useCallback(
    (parentId: string | null | undefined): number => nextSortOrderFor(units, parentId),
    [units]
  );

  const createUnit = useCallback(
    async (input: CreateUnitInput): Promise<OrganizationalUnit | null> => {
      if (!orgId) return null;

      try {
        const { data, error: insertError } = await supabase
          .from('organizational_units')
          .insert({
            organization_id: orgId,
            parent_id: input.parent_id || null,
            unit_type: input.unit_type,
            name: input.name,
            code: input.code || null,
            description: input.description || null,
            color: input.color || null,
            icon: input.icon || null,
            sort_order: input.sort_order ?? nextSortOrder(input.parent_id),
          })
          .select()
          .single();

        if (insertError) throw insertError;

        await fetchUnits();
        return data as OrganizationalUnit;
      } catch (err: any) {
        console.error('Error creating unit:', err);
        setError(err.message);
        return null;
      }
    },
    [orgId, fetchUnits, nextSortOrder]
  );

  const updateUnit = useCallback(
    async (unitId: string, input: UpdateUnitInput): Promise<boolean> => {
      try {
        const { error: updateError } = await supabase
          .from('organizational_units')
          .update(input)
          .eq('id', unitId);

        if (updateError) throw updateError;

        await fetchUnits();
        return true;
      } catch (err: any) {
        console.error('Error updating unit:', err);
        setError(err.message);
        return false;
      }
    },
    [fetchUnits]
  );

  /**
   * Soft-delete a unit, re-parenting its direct children onto the deleted node's
   * own parent.
   *
   * Without the re-parent step the children keep pointing at a row that
   * fetchUnits no longer returns, so buildTree treats them as roots: deleting a
   * mid-level department silently promoted its whole subtree to the top of the
   * chart. Re-parenting keeps the surviving hierarchy intact and makes the
   * operation mean what the UI says it means ("remove this element").
   */
  const deleteUnit = useCallback(
    async (unitId: string): Promise<boolean> => {
      try {
        const children = units.filter((u) => u.parent_id === unitId);

        if (children.length > 0) {
          const { error: reparentError } = await supabase
            .from('organizational_units')
            .update({ parent_id: reparentTarget(units, unitId) })
            .eq('parent_id', unitId);

          if (reparentError) throw reparentError;
        }

        // The deleted row keeps its own parent_id: it is already excluded from
        // the tree by is_active, and preserving the link is what makes a later
        // reactivation land back where it was.
        const { error: deleteError } = await supabase
          .from('organizational_units')
          .update({ is_active: false })
          .eq('id', unitId);

        if (deleteError) throw deleteError;

        await fetchUnits();
        return true;
      } catch (err: any) {
        console.error('Error deleting unit:', err);
        setError(err.message);
        return false;
      }
    },
    [fetchUnits, units]
  );

  /** Units below this one at any depth — used to warn before a delete. */
  const countDescendants = useCallback(
    (unitId: string): number => countDescendantsOf(units, unitId),
    [units]
  );

  const saveProfile = useCallback(
    async (input: Partial<OrganizationProfile>, opts?: SaveProfileOpts): Promise<boolean> => {
      if (!orgId) return false;

      try {
        const { error: upsertError } = await supabase.from('organization_profiles').upsert(
          {
            organization_id: orgId,
            ...input,
            zrodla: zrodlaDoZapisu(profile, input, opts),
          },
          { onConflict: 'organization_id' }
        );

        if (upsertError) throw upsertError;

        await fetchProfile();
        return true;
      } catch (err: any) {
        console.error('Error saving profile:', err);
        setError(err.message);
        return false;
      }
    },
    [orgId, profile, fetchProfile]
  );

  // Create multiple units at once (for onboarding)
  const createBulkUnits = useCallback(
    async (inputs: CreateUnitInput[]): Promise<boolean> => {
      if (!orgId || inputs.length === 0) return true;

      try {
        const rows = inputs.map((input, idx) => ({
          organization_id: orgId,
          parent_id: input.parent_id || null,
          unit_type: input.unit_type,
          name: input.name,
          code: input.code || null,
          description: input.description || null,
          color: input.color || null,
          sort_order: input.sort_order ?? idx,
        }));

        const { error: insertError } = await supabase.from('organizational_units').insert(rows);

        if (insertError) throw insertError;

        await fetchUnits();
        return true;
      } catch (err: any) {
        console.error('Error creating bulk units:', err);
        setError(err.message);
        return false;
      }
    },
    [orgId, fetchUnits]
  );

  return {
    units,
    tree,
    profile,
    isLoading,
    error,
    createUnit,
    updateUnit,
    deleteUnit,
    countDescendants,
    saveProfile,
    createBulkUnits,
    refresh: fetchAll,
  };
}
